import { takeLatest, all, call, put, select } from 'redux-saga/effects';
import {
    startUserRoleDataFetch,
    setUserRoleDataSuccess,
    setUserRoleDataError,
    startUserDataFetch,
    setUserDataSuccess,
    setUserDataError,
    startAddUserFetch,
    setAddUserSuccess,
    setUpdateUserRole,
    startUpdateUserFetch,
    setUpdateUserSuccess,
    setUpdateUserError,
    startDeleteUserFetch,
    setDeleteUserSuccess,
    setDeleteUserError,
    startFetchSingleUser,
    setSingleUserData,
    updateUserRole,
    completedFetch
} from './userRoleSlice';
import {
    fetchUserRolesAPI,
    fetchUsersAPI,
    addUserAPI,
    updateUserAPI,
    deleteUserAPI,
    fetchSingleUserAPI
} from 'Api/UserRoleAPI/userRoleApi';
import { updateUserRoleApi } from 'Api/UserAuthAPI/userAuth';
import { setNotificationAction } from '../NotificationStore/notification.slice.selector';

function* startUserRoleDataWatcher() {
    yield takeLatest(startUserRoleDataFetch.type, setAllUserRoleData);
}

function* setAllUserRoleData({ payload }) {
    try {
        const allUserRoleData = yield call(fetchUserRolesAPI, { data: { payload } });
        yield put(setUserRoleDataSuccess(allUserRoleData));
        yield put(completedFetch());
    } catch (error) {
        const errorMessage = error?.message || 'Failed to fetch user roles';
        yield put(setUserRoleDataError(errorMessage));
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

function* updateRoleWatcher() {
    yield takeLatest(updateUserRole.type, setUpdateRole);
}

function* setUpdateRole({ payload }) {
    try {
        let data = yield call(updateUserRoleApi, payload);
        yield put(setUpdateUserRole(data));

        if (data?.affectedRows === 1) {
            yield put(
                setNotificationAction({
                    message: 'Role Updated Successfully',
                    variant: 'success'
                })
            );
        } else if (data?.affectedRows === 0) {
            yield put(
                setNotificationAction({
                    message: 'Role Not Updated',
                    variant: 'error'
                })
            );
        }

        const state = yield select((state) => state.UserRole);
        const fetchPayload = {
            page: state?.pageIndex || 1,
            limit: state?.pageSize || 10,
            name: '',
            email: '',
            ...(state?.filterData || {})
        };
        yield put(startUserRoleDataFetch(fetchPayload));
        yield put(completedFetch());
    } catch (error) {
        const errMsg = error?.response?.data?.message || error?.message || 'Failed to update role';
        yield put(
            setNotificationAction({
                message: errMsg,
                variant: 'error'
            })
        );
    }
}

function* startUserDataWatcher() {
    yield takeLatest(startUserDataFetch.type, setUserData);
}

function* setUserData({ payload }) {
    try {
        const userData = yield call(fetchUsersAPI, payload);
        yield put(setUserDataSuccess(userData));
    } catch (error) {
        const errorMessage = error?.message || 'Failed to fetch users';
        yield put(setUserDataError(errorMessage));
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

// (3) Watcher - Fetch Single User
function* startSingleUserDataWatcher() {
    yield takeLatest(startFetchSingleUser.type, setSingleUserDataHandler);
}

// (3) Next - Fetch Single User
function* setSingleUserDataHandler({ payload }) {
    try {
        const singleUserData = yield call(fetchSingleUserAPI, payload);
        yield put(setSingleUserData(singleUserData));
    } catch (error) {
        const errorMessage = error?.message || 'Failed to fetch user details';
        yield put(setUserDataError(errorMessage));
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

// (4) Watcher - Add User
function* startAddUserWatcher() {
    yield takeLatest(startAddUserFetch.type, setAddUserData);
}

// (4) Next - Add User
function* setAddUserData({ payload }) {
    try {
        const { onSuccess } = payload;
        const newUserData = yield call(addUserAPI, payload);
        yield put(setAddUserSuccess(newUserData));
        yield put(
            setNotificationAction({
                message: 'User added successfully!',
                variant: 'success'
            })
        );
        if (onSuccess) onSuccess();
    } catch (error) {
        const errorMessage = error;
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

// (5) Watcher - Update User
function* startUpdateUserWatcher() {
    yield takeLatest(startUpdateUserFetch.type, setUpdateUserData);
}

// (5) Next - Update User
function* setUpdateUserData({ payload }) {
    try {
        const { id, ...userData } = payload;
        const updatedUserData = yield call(updateUserAPI, id, userData);
        yield put(setUpdateUserSuccess(updatedUserData));
        yield put(
            setNotificationAction({
                message: 'User updated successfully!',
                variant: 'success'
            })
        );
    } catch (error) {
        const errorMessage = error?.message || 'Failed to update user';
        yield put(setUpdateUserError(errorMessage));
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

// (6) Watcher - Delete User
function* startDeleteUserWatcher() {
    yield takeLatest(startDeleteUserFetch.type, setDeleteUserData);
}

// (6) Next - Delete User
function* setDeleteUserData({ payload }) {
    try {
        yield call(deleteUserAPI, payload);
        yield put(setDeleteUserSuccess(payload));
        yield put(
            setNotificationAction({
                message: 'User deleted successfully!',
                variant: 'success'
            })
        );
        // Refetch users list after deletion
        yield put(startUserDataFetch({ pageIndex: 1, pageSize: 10 }));
    } catch (error) {
        const errorMessage = error?.message || 'Failed to delete user';
        yield put(setDeleteUserError(errorMessage));
        yield put(
            setNotificationAction({
                message: errorMessage,
                variant: 'error'
            })
        );
    }
}

// Root Saga - Accumulator (using yield all pattern)
export default function* userRoleSaga() {
    yield all([
        call(startUserRoleDataWatcher),
        call(startUserDataWatcher),
        call(startSingleUserDataWatcher),
        call(startAddUserWatcher),
        call(startUpdateUserWatcher),
        call(startDeleteUserWatcher),
        call(updateRoleWatcher)
    ]);
}
